import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, SimpleGrid, Spinner,
  Stat, StatNumber, StatHelpText,
  Badge, Icon, Alert, AlertIcon, Button, Progress, Tooltip,
  Table, Thead, Tbody, Tr, Th, Td, useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDollarSign, FiTrendingUp, FiCalendar, FiTag, FiGift, FiFileText } from 'react-icons/fi';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, Legend,
} from 'recharts';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

export const formatMoney = (amount, currency = 'USD') =>
  amount === null || amount === undefined
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

export const formatAgo = (iso, now = Date.now()) => {
  if (!iso) return 'never';
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
};

const thresholdColor = (fraction) => (fraction >= 1 ? 'red' : fraction >= 0.8 ? 'orange' : 'green');

const StatCard = ({ label, value, help, icon, color }) => (
  <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderColor={`${color}.400`}>
    <HStack justify="space-between" mb={2}>
      <Text fontSize="sm" color="gray.500" fontWeight="semibold">{label}</Text>
      <Icon as={icon} color={`${color}.400`} boxSize={5} />
    </HStack>
    <Stat>
      <StatNumber fontSize="3xl">{value}</StatNumber>
      {help && <StatHelpText mb={0}>{help}</StatHelpText>}
    </Stat>
  </Box>
);

function Thresholds({ thresholds, monthToDate, forecast, currency }) {
  if (!thresholds?.length) return null;
  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
      <Heading size="sm" mb={1}>Billing alarms</Heading>
      <Text fontSize="sm" color="gray.500" mb={4}>
        This month's usage (before credits) against the account's billing alarm thresholds.
      </Text>
      <VStack align="stretch" spacing={4}>
        {thresholds.map((threshold) => {
          const fraction = monthToDate / threshold;
          const forecastOver = forecast !== null && forecast !== undefined && forecast >= threshold;
          return (
            <Box key={threshold} data-testid={`threshold-${threshold}`}>
              <HStack justify="space-between" mb={1}>
                <Text fontWeight="semibold">{formatMoney(threshold, currency)}</Text>
                <HStack>
                  {forecastOver && fraction < 1 && <Badge colorScheme="orange">Forecast to pass</Badge>}
                  {fraction >= 1 && <Badge colorScheme="red">Passed</Badge>}
                  <Text fontSize="sm" color="gray.600">{Math.round(fraction * 100)}%</Text>
                </HStack>
              </HStack>
              <Progress value={Math.min(fraction * 100, 100)} colorScheme={thresholdColor(fraction)} borderRadius="md" size="sm" />
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

function Costs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/billing/`, { headers: getAuthHeaders() })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (res.status === 403) throw new Error('Only super admins can see AWS costs.');
        if (res.status === 404) throw new Error(body.reason || 'AWS billing is not available here.');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return body;
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    fetch(`${API_BASE}/billing/refresh/`, { method: 'POST', headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((body) => {
        setData(body);
        if (body.throttled) {
          toast({ status: 'info', title: 'Refreshed recently', description: 'Cost Explorer can be refreshed every 30 minutes.' });
        } else if (body.error) {
          toast({ status: 'error', title: 'Refresh failed', description: body.error });
        } else {
          toast({ status: 'success', title: 'Costs refreshed' });
        }
      })
      .catch((err) => toast({ status: 'error', title: 'Refresh failed', description: err.message }))
      .finally(() => setRefreshing(false));
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" /></Box>;
  }
  if (error) {
    return <Alert status="warning" borderRadius="lg"><AlertIcon />{error}</Alert>;
  }

  const summary = data.summary;
  const currency = summary?.currency || 'USD';
  const refreshAvailableAt = data.manual_refresh_available_at ? new Date(data.manual_refresh_available_at) : null;
  const refreshLocked = refreshAvailableAt && refreshAvailableAt > new Date();

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" flexWrap="wrap" spacing={4}>
        <Box>
          <Heading size="lg">AWS Costs</Heading>
          <Text color="gray.500" fontSize="sm">
            Account {data.account_id} · updated {formatAgo(data.fetched_at)} · cached for {data.cache_hours} hours
            (each refresh costs $0.03 in Cost Explorer fees)
          </Text>
        </Box>
        <Tooltip
          isDisabled={!refreshLocked}
          label={refreshLocked ? `Available again at ${refreshAvailableAt.toLocaleTimeString()}` : ''}
        >
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={refresh}
            isLoading={refreshing}
            isDisabled={refreshLocked}
            size="sm"
          >
            Refresh now
          </Button>
        </Tooltip>
      </HStack>

      {data.error && (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {summary ? 'Showing the last figures fetched; the latest refresh failed: ' : 'Could not load costs: '}
          {data.error}
        </Alert>
      )}

      {summary && (
        <>
          {summary.estimated && (
            <Text fontSize="sm" color="gray.500">
              This month's figures are estimates until AWS finalises the bill.
            </Text>
          )}

          {/* Usage is what the infrastructure costs; credits are taken off it; the bill is the rest.
              (Snapshots cached before credits were split out have no credits/net fields: shown as —.) */}
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
            <StatCard label="Usage this month" value={formatMoney(summary.month_to_date, currency)} help={`Before credits, since ${summary.month_start}`} icon={FiDollarSign} color="blue" />
            <StatCard label="Credits applied" value={formatMoney(summary.credits_month_to_date, currency)} help="This month" icon={FiGift} color="teal" />
            <StatCard label="Bill this month" value={formatMoney(summary.net_month_to_date, currency)} help="Usage after credits" icon={FiFileText} color="orange" />
            <StatCard label="Forecast usage for the month" value={formatMoney(summary.forecast_month_end, currency)} help={summary.forecast_month_end === null ? 'Not enough history yet' : 'AWS forecast, before credits'} icon={FiTrendingUp} color="purple" />
            <StatCard
              label="Last month"
              value={formatMoney(summary.last_month_total, currency)}
              help={summary.last_month_net === undefined || summary.last_month_net === null ? 'Usage' : `Usage · billed ${formatMoney(summary.last_month_net, currency)}`}
              icon={FiCalendar}
              color="gray"
            />
            <StatCard
              label={`Tagged ${summary.project_tag.key}=${summary.project_tag.value}`}
              value={formatMoney(summary.project_tag.month_to_date, currency)}
              help="Month to date"
              icon={FiTag}
              color="green"
            />
          </SimpleGrid>

          <Thresholds
            thresholds={data.thresholds}
            monthToDate={summary.month_to_date}
            forecast={summary.forecast_month_end}
            currency={currency}
          />

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
            <Heading size="sm" mb={1}>Daily usage, last 30 days</Heading>
            <Text fontSize="sm" color="gray.500" mb={4}>Before credits.</Text>
            <Box height="260px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={summary.daily.map((d) => ({ ...d, other: Math.max(0, +(d.total - d.tagged).toFixed(2)) }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => formatMoney(v, currency)} width={70} />
                  <ChartTooltip formatter={(v) => formatMoney(v, currency)} />
                  <Legend />
                  <Bar dataKey="tagged" name={`${summary.project_tag.key}=${summary.project_tag.value}`} stackId="spend" fill="#38A169" />
                  <Bar dataKey="other" name="Untagged" stackId="spend" fill="#A0AEC0" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="md" overflowX="auto">
            <Heading size="sm" mb={4}>By service, this month</Heading>
            {summary.by_service.length === 0 ? (
              <Text color="gray.500">No spend yet this month.</Text>
            ) : (
              <Table size="sm">
                <Thead><Tr><Th>Service</Th><Th isNumeric>Spend</Th><Th isNumeric>Share</Th></Tr></Thead>
                <Tbody>
                  {summary.by_service.map(({ service, amount }) => (
                    <Tr key={service}>
                      <Td>{service}</Td>
                      <Td isNumeric>{formatMoney(amount, currency)}</Td>
                      <Td isNumeric>
                        {summary.month_to_date > 0 ? `${Math.round((amount / summary.month_to_date) * 100)}%` : '—'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>
        </>
      )}
    </VStack>
  );
}

export default Costs;
